import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { easing } from './easing';

gsap.registerPlugin(ScrollTrigger);

export function byMotionId(root: ParentNode, id: string): HTMLElement | null {
  return root.querySelector<HTMLElement>(`[data-motion-id="${id}"]`);
}

interface CurtainReveal {
  trigger: string;
  front: string;
  back?: string;
}
// These are authored image masks, not arbitrary interaction records.
const imageReveals: CurtainReveal[] = [
  {
    trigger: '0549ac50-c85c-23ad-b741-c1f32b27af13',
    front: '8ad133b7-f9f4-7e0c-7878-82b79a84c14b',
    back: 'e15c27e6-c4cc-a8cf-b3c2-c42358ef9942',
  },
  {
    trigger: '8743ad6e-c302-46fc-a887-02b5404664fa',
    front: '51b7b609-9149-0b40-0c74-357be67267fe',
    back: '349cb8b3-8d97-e205-f06f-8f7b765f88fd',
  },
  {
    trigger: '78959cb6-d57e-81c9-073b-8e3d840056b2',
    front: 'a96d7a30-ad6a-751d-a678-e49a064cc942',
    back: '4b749ea9-88f0-0432-1e76-5388c2381e36',
  },
  {
    trigger: '5482d63d-9e65-4b00-7d44-e839c31d1a99',
    front: '9cf7bdeb-a8e6-d77f-a13a-e5f6bb7fafe1',
    back: 'a58e1068-6856-812a-b4e8-e6badb8e181e',
  },
  {
    trigger: 'eae23e5e-4fda-6040-39d8-9a7952d11cbb',
    front: 'f2b3c786-e8b6-35a8-e32a-f59cb6dd33fc',
    back: '532de25b-2f2e-0a52-a8a8-76feaef4dec0',
  },
  {
    trigger: '9cdf75c3-22b0-c241-6e4d-78c785cc39c8',
    front: '97790906-1540-c8e3-c626-fc8a709fab33',
    back: '6584abfd-8b42-72ed-6f76-161adedd9760',
  },
  {
    trigger: 'c29bb6d3-8070-a04b-7ce5-bd37a5e8499f',
    front: 'a6978f29-a372-3e54-c47a-e21ffcfcafd9',
    back: '7bd459f9-e71f-eaa6-4901-e2f70f55cbf5',
  },
  {
    trigger: '09ece5ef-4b55-49d8-2411-e9cec04aaed9',
    front: 'a9b68497-58e4-526f-0266-ef95568a20af',
  },
  {
    trigger: '86731408-a2fd-b9ad-5f64-1c7cbeedf9c2',
    front: '0ec5cfbb-69c3-5a03-470d-ed13a2b26074',
  },
  {
    trigger: 'd61bd79a-9f99-ddf9-30ad-16bd9fb74d1e',
    front: 'd61bd79a-9f99-ddf9-30ad-16bd9fb74d1f',
  },
  {
    trigger: 'c6adcadd-85fc-e2c8-8847-1478b7d486f9',
    front: '9580cc5d-5030-208e-c3e1-0c8b7db73aab',
  },
];

function revealTimeline(trigger: HTMLElement, reduced: boolean) {
  return gsap.timeline({
    defaults: { ease: easing.cubicOut },
    ...(reduced ? {} : { scrollTrigger: { trigger, start: 'top bottom', once: true } }),
  });
}

/** Authored one-shot IX2 scroll reveals; all source offsets are zero. */
export function setupReveals(root: HTMLElement, reduced: boolean) {
  const duration = (seconds: number) => (reduced ? 0 : seconds);
  const delay = duration;
  imageReveals.forEach(({ trigger: triggerId, front: frontId, back: backId }) => {
    const trigger = byMotionId(root, triggerId);
    const front = byMotionId(root, frontId);
    const back = backId ? byMotionId(root, backId) : null;
    if (!trigger || !front) return;
    gsap.set([front, ...(back ? [back] : [])], { width: reduced ? '0%' : '100%' });
    const timeline = revealTimeline(trigger, reduced).to(
      front,
      { width: '0%', duration: duration(1) },
      0,
    );
    if (back) timeline.to(back, { width: '0%', duration: duration(0.8) }, delay(0.5));
  });

  [
    '718e2422-b200-a35c-3893-ca7b7b5fd452',
    'c1163ffc-27dd-dfdf-6010-edf6b9519424',
    '38a8c934-9af8-b351-bad0-07abd30720b9',
  ].forEach((id) => {
    const divider = byMotionId(root, id);
    if (!divider) return;
    gsap.set(divider, { width: reduced ? '100%' : '0%' });
    revealTimeline(divider, reduced).to(divider, { width: '100%', duration: duration(1) });
  });

  const hero = byMotionId(root, 'e12b7f09-3add-a0b4-700e-c9a343bf9240');
  const heroDivider = byMotionId(root, '6098505e-657d-77dd-1be5-0d17a686bf3d');
  const heroLogo = byMotionId(root, 'c3c2a070-cf83-9c72-2bb7-1f1678c8af2a');
  if (hero && heroDivider && heroLogo) {
    gsap.set(heroDivider, { width: reduced ? '100%' : '0%' });
    gsap.set(heroLogo, { height: reduced ? '3rem' : 0 });
    revealTimeline(hero, reduced)
      .to(heroDivider, { width: '100%', duration: duration(1.5) }, 0)
      .to(heroLogo, { height: '3rem', duration: duration(1) }, delay(0.5));
  }

  const fades: [string, number, number, string][] = [
    ['412fbdbd-9651-c31c-0f75-035e2f30a196', 0.5, 0.5, 'none'],
    ['2fe184b9-ecae-d3a9-0bf6-d005c47c5a65', 0.5, 0.5, 'none'],
    ['f45ed141-6428-9764-8468-a23b310cb164', 0.5, 1, easing.cubicOut],
  ];
  fades.forEach(([id, wait, length, ease]) => {
    const target = byMotionId(root, String(id));
    if (!target) return;
    gsap.set(target, { opacity: reduced ? 1 : 0 });
    revealTimeline(target, reduced).to(
      target,
      { opacity: 1, duration: duration(Number(length)), ease },
      delay(Number(wait)),
    );
  });

  const modeNumber = byMotionId(root, 'd92e6e4b-4e94-c9b3-9532-3eb79afe3733');
  if (modeNumber) {
    gsap.set(modeNumber, { height: reduced ? 'auto' : 0 });
    revealTimeline(modeNumber, reduced).to(
      modeNumber,
      { height: 'auto', duration: duration(0.8) },
      delay(0.2),
    );
  }

  const flag = byMotionId(root, '1dd70e6c-4cda-5ca7-74ee-af8329192f35');
  if (flag) {
    gsap.set(flag, { opacity: reduced ? 1 : 0 });
    const timeline = revealTimeline(flag, reduced).to(flag, {
      opacity: 1,
      duration: duration(0.5),
      ease: 'none',
    });
    const cross = flag.querySelector('.ch_flag-cross');
    if (cross) timeline.to(cross, { rotation: 360, duration: duration(1.5) });
  }
  setupMenuReveals(root, reduced);
}

function setupMenuReveals(root: HTMLElement, reduced: boolean) {
  const duration = (seconds: number) => (reduced ? 0 : seconds);
  const heroMenu = byMotionId(root, 'a8b7271e-b699-d5a9-905a-e7bfdd9312a0');
  if (heroMenu) {
    const texts = heroMenu.querySelectorAll('.hero_text');
    const icons = heroMenu.querySelectorAll('.menu_icon.is-light');
    const iconText = heroMenu.querySelectorAll('.menu_icon-text');
    const dividers = heroMenu.querySelectorAll('.menu-divider');
    gsap.set(texts, { y: 0, yPercent: reduced ? 0 : -100 });
    gsap.set(icons, { height: reduced ? 'auto' : 0 });
    gsap.set(iconText, { opacity: reduced ? 1 : 0 });
    gsap.set(dividers, { width: reduced ? '100%' : '0%' });
    revealTimeline(heroMenu, reduced)
      .to(texts, { yPercent: 0, duration: duration(1) }, 0)
      .to(dividers, { width: '100%', duration: duration(1), stagger: duration(0.3) }, 0)
      .to(icons, { height: 'auto', duration: duration(0.5) }, duration(0.3))
      .to(iconText, { opacity: 1, duration: duration(0.5) }, duration(0.6));
  }
  const footer = byMotionId(root, 'd61bd79a-9f99-ddf9-30ad-16bd9fb74d38');
  if (footer) {
    const texts = footer.querySelectorAll('.footer_text');
    const icons = footer.querySelectorAll('.menu_icon');
    const labels = footer.querySelectorAll('.menu_icon-text, .icon-embed-custom');
    const dividers = footer.querySelectorAll('.footer-divider');
    const heading = byMotionId(root, 'd61bd79a-9f99-ddf9-30ad-16bd9fb74d3a');
    const logo = byMotionId(root, 'd61bd79a-9f99-ddf9-30ad-16bd9fb74d35');
    gsap.set(texts, { y: 0, yPercent: reduced ? 0 : -100 });
    gsap.set(icons, { height: reduced ? 'auto' : 0 });
    gsap.set(labels, { opacity: reduced ? 1 : 0 });
    gsap.set(dividers, { width: reduced ? '100%' : '0%' });
    if (heading) gsap.set(heading, { opacity: reduced ? 1 : 0 });
    if (logo) gsap.set(logo, { opacity: reduced ? 1 : 0 });
    const timeline = revealTimeline(footer, reduced)
      .to(texts, { yPercent: 0, duration: duration(1) }, 0)
      .to(icons, { height: 'auto', duration: duration(1) }, 0)
      .to(dividers, { width: '100%', duration: duration(1), stagger: duration(0.2) }, 0)
      .to(labels, { opacity: 1, duration: duration(1) }, duration(0.6));
    if (heading) timeline.to(heading, { opacity: 1, duration: duration(1) }, duration(0.5));
    if (logo) timeline.to(logo, { opacity: 1, duration: duration(0.5) }, duration(0.6));
  }
}
